import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Grid,
  MenuItem,
  Select,
  FormControl,
} from '@mui/material';
import { ArrowBack, Star, StarBorder, Hotel, AttachMoney, ShowChart, MeetingRoom } from '@mui/icons-material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMarketKPIs } from '../hooks/useMarketData';
import { usePageTitle } from '../hooks/usePageTitle';
import { useFavorites } from '../hooks/useFavorites';
import { EnhancedKPICard } from '../components/cards/EnhancedKPICard';
import { EnhancedAirportCard } from '../components/cards/EnhancedAirportCard';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { ErrorMessage } from '../components/common/ErrorMessage';

const CLASSIFICATIONS = [
  'All',
  'Luxury',
  'Upper Upscale',
  'Upscale',
  'Upper Midscale',
  'Midscale',
  'Economy'
];

export const MarketDetail: React.FC = () => {
  const navigate = useNavigate();
  const { marketId } = useParams<{ marketId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [classification, setClassification] = useState(searchParams.get('classification') || 'All');
  const { data, isLoading, error } = useMarketKPIs(marketId ? parseInt(marketId) : null, classification);
  const { isFavorite, toggleFavorite } = useFavorites();

  const favorite = marketId ? isFavorite(parseInt(marketId)) : false;

  usePageTitle(data?.market?.name || 'Market Details');

  // Update URL when classification changes
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    if (classification !== 'All') {
      params.set('classification', classification);
    } else {
      params.delete('classification');
    }
    setSearchParams(params, { replace: true });
  }, [classification]);

  if (isLoading) {
    return <LoadingSpinner message="Loading market data..." />;
  }

  if (error || !data) {
    return <ErrorMessage message="Failed to load market data" />;
  }

  const { market, kpis, airports } = data;

  return (
    <Box pb={10}>
      <Box px={2} pt={2} mb={3}>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <IconButton onClick={() => navigate(`/markets/${market.country_id}`)}>
            <ArrowBack />
          </IconButton>
          <Box flex={1}>
            <Typography variant="h5" fontWeight="bold">
              {market.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {market.country} • {market.continent}
            </Typography>
          </Box>
          <IconButton
            onClick={() => toggleFavorite(market.id)}
            sx={{ color: favorite ? 'warning.main' : 'action.disabled' }}
          >
            {favorite ? <Star /> : <StarBorder />}
          </IconButton>
        </Box>
      </Box>

      <Box px={2}>
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <Typography variant="h6">
            Hotel Class:
          </Typography>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <Select
              value={classification}
              onChange={(e) => setClassification(e.target.value)}
              displayEmpty
            >
              {CLASSIFICATIONS.map((cls) => (
                <MenuItem key={cls} value={cls}>
                  {cls}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Grid container spacing={2} mb={3}>
          {/* Occupancy Card - Monthly Only */}
          <Grid item xs={12}>
            <EnhancedKPICard
              title="Occupancy"
              period={kpis.occupancy.current_month?.period || ''}
              value={kpis.occupancy.current_month?.value ?? null}
              unit="%"
              previousYear={kpis.occupancy.current_month?.previous_year ?? null}
              changePercent={kpis.occupancy.current_month?.change_percent ?? null}
              vsPreCovid={null}
              vsCovidLow={null}
              onClick={() => {
                const params = classification !== 'All' ? `?classification=${encodeURIComponent(classification)}` : '';
                navigate(`/market/${market.id}/kpi/occupancy${params}`);
              }}
              icon={<Hotel sx={{ fontSize: 20 }} />}
            />
          </Grid>

          {/* ADR Card - Monthly Only */}
          <Grid item xs={12}>
            <EnhancedKPICard
              title="ADR"
              period={kpis.adr.current_month?.period || ''}
              value={kpis.adr.current_month?.value ?? null}
              currency={kpis.adr.current_month?.currency}
              previousYear={kpis.adr.current_month?.previous_year ?? null}
              changePercent={kpis.adr.current_month?.change_percent ?? null}
              vsPreCovid={null}
              vsCovidLow={null}
              onClick={() => {
                const params = classification !== 'All' ? `?classification=${encodeURIComponent(classification)}` : '';
                navigate(`/market/${market.id}/kpi/adr${params}`);
              }}
              icon={<AttachMoney sx={{ fontSize: 20 }} />}
            />
          </Grid>

          {/* RevPAR Card - Monthly Only */}
          <Grid item xs={12}>
            <EnhancedKPICard
              title="RevPAR"
              period={kpis.revpar.current_month?.period || ''}
              value={kpis.revpar.current_month?.value ?? null}
              currency={kpis.revpar.current_month?.currency}
              previousYear={kpis.revpar.current_month?.previous_year ?? null}
              changePercent={kpis.revpar.current_month?.change_percent ?? null}
              vsPreCovid={null}
              vsCovidLow={null}
              onClick={() => {
                const params = classification !== 'All' ? `?classification=${encodeURIComponent(classification)}` : '';
                navigate(`/market/${market.id}/kpi/revpar${params}`);
              }}
              icon={<ShowChart sx={{ fontSize: 20 }} />}
            />
          </Grid>

          {/* Rooms Card - Monthly Only */}
          {kpis.rooms && (
            <Grid item xs={12}>
              <EnhancedKPICard
                title="Rooms"
                period={kpis.rooms.current_month?.period || ''}
                value={kpis.rooms.current_month?.value ?? null}
                unit="rooms"
                previousYear={kpis.rooms.current_month?.previous_year ?? null}
                changePercent={kpis.rooms.current_month?.change_percent}
                vsPreCovid={null}
                vsCovidLow={null}
                onClick={() => {
                  const params = classification !== 'All' ? `?classification=${encodeURIComponent(classification)}` : '';
                  navigate(`/market/${market.id}/kpi/rooms${params}`);
                }}
                icon={<MeetingRoom sx={{ fontSize: 20 }} />}
              />
            </Grid>
          )}
        </Grid>

        {airports && airports.length > 0 && (
          <>
            <Box mb={3} mt={4}>
              <Typography variant="h6">
                Airport Traffic
              </Typography>
            </Box>

            <Grid container spacing={2}>
              {airports.map((airport) => (
                <Grid item xs={12} key={airport.code}>
                  <EnhancedAirportCard
                    airportCode={airport.code}
                    airportName={airport.name}
                    city={airport.city}
                    state={airport.state}
                    distance_km={airport.distance_km}
                    passengers={airport.passengers}
                    viewMode="current"
                    onClick={() => navigate(`/market/${market.id}/airport/${airport.code}`)}
                  />
                </Grid>
              ))}
            </Grid>
          </>
        )}
      </Box>
    </Box>
  );
};
